import { View, html, CustomEventWithDetail } from 'rune-ts';
import klass from './SingleSelectButtonGroup.module.scss';

export type SelectableItem<T = unknown> = {
  id: string;
  display_name: string;
  value?: T;
};

export type SingleSelectButtonGroupData<T> = {
  items: SelectableItem<T>[];
  selected_id?: string;
};

export interface SingleSelectButtonGroupOnChangeHandler<T> {
  onChange?: (selected: SelectableItem<T>) => void;
}

/**
 * 다른 핸들러 추가되면 여기에 union
 */
export type SingleSelectButtonGroupHandler<T> = SingleSelectButtonGroupOnChangeHandler<T>;

export interface SingleSelectButtonGroupOption<T = unknown> extends SingleSelectButtonGroupHandler<T> {
  container_class?: string;
  button_class?: string;
  active_class?: string;
}

export class SingleSelectChangeEvent<T> extends CustomEventWithDetail<SelectableItem<T>> {}

export class SingleSelectButtonGroup<T> extends View<SingleSelectButtonGroupData<T>> {
  private selected_id: string;

  constructor(
    data: SingleSelectButtonGroupData<T>,
    private option: SingleSelectButtonGroupOption<T> = {},
  ) {
    super(data);
    this.selected_id = data.selected_id ?? data.items[0]?.id ?? '';
  }

  override template() {
    const { container_class = '', button_class = '', active_class = '' } = this.option;

    return html`
      <div class="${klass.container} ${container_class}">
        ${this.data.items.map(
          ({ id, display_name }) => html`
            <button
              type="button"
              class="${klass.button} ${button_class} ${id === this.selected_id ? active_class : ''}"
              data-id="${id}"
            >
              ${display_name}
            </button>
          `,
        )}
      </div>
    `;
  }

  override onRender() {
    this.delegate('click', 'button[data-id]', (e) => {
      const id = (e.target as HTMLButtonElement).dataset.id;
      if (id && id !== this.selected_id) {
        const item = this.data.items.find((item) => item.id === id);
        if (!item) return;

        this.selected_id = id;
        this.redraw();

        this.dispatchEvent(SingleSelectChangeEvent<T>, {
          bubbles: true,
          detail: item,
        });

        this.option.onChange?.(item);
      }
    });
  }

  public getSelected(): SelectableItem<T> | undefined {
    return this.data.items.find((item) => item.id === this.selected_id);
  }
}
